/**
 * OS RELATÓRIOS DO PROFESSOR, EM GRUPOS — uma lista só, lida pela barra lateral (`TeacherSidebar`) e pela página de
 * relatórios (`RelatoriosPage`).
 *
 * PELAS PERGUNTAS DO PROFESSOR (25/10/2026, pedido do pesquisador — relatórios genéricos, item 10): cada grupo é uma
 * pergunta, com uma frase curta, e nenhum relatório saiu. Os nomes são neutros ("Por Habilidade", "Mapa de Habilidades",
 * "Evolução por Habilidade"): o relatório serve ao SAEB, à BNCC, ao Enem e ao conteúdo, e o título da página diz a matriz
 * em uso. ("Descritores SAEB", do acervo, não é relatório e não muda.)
 */
export type ItemDeRelatorio = { path: string; label: string; frase: string };
export type GrupoDeRelatorio = { coluna: 1 | 2; titulo: string; frase?: string; itens: ItemDeRelatorio[] };

export const GRUPOS_DE_RELATORIO: GrupoDeRelatorio[] = [
  { coluna: 1, titulo: 'Como foi a prova?', frase: 'O resultado de uma aplicação, da turma e de cada aluno.', itens: [
    { path: '/teacher/reports', label: 'Por Simulado', frase: 'Participação, médias, questões e distratores de uma aplicação' },
    { path: '/teacher/reports/proficiency', label: 'Níveis de Desempenho', frase: 'Quantos alunos em cada faixa de acerto' },
    { path: '/teacher/reports/class-grades', label: 'Notas por Turma', frase: 'As notas de cada aluno em cada simulado, com CSV' },
  ] },
  { coluna: 2, titulo: 'O que a turma não aprendeu?', frase: 'O acerto por habilidade — SAEB, BNCC, Enem ou conteúdo.', itens: [
    { path: '/teacher/reports/descriptors', label: 'Por Habilidade', frase: 'O acerto por habilidade numa aplicação' },
    { path: '/teacher/reports/descriptor-heatmap', label: 'Mapa de Habilidades', frase: 'Habilidade × turma, em cores' },
    { path: '/teacher/reports/comparar-turmas', label: 'Comparar Turmas', frase: 'As turmas lado a lado no mesmo simulado' },
  ] },
  { coluna: 1, titulo: 'Quem precisa de ajuda?', frase: 'Um aluno de cada vez.', itens: [
    { path: '/teacher/reports/students', label: 'Por Aluno', frase: 'A trajetória de um aluno nos simulados' },
  ] },
  { coluna: 1, titulo: 'O que fazer agora?', frase: 'O próximo passo, pelo que a turma errou.', itens: [
    { path: '/teacher/reports/next-simulation', label: 'Próximo Simulado', frase: 'As habilidades a retomar, e um simulado com elas' },
  ] },
  { coluna: 2, titulo: 'Está melhorando?', frase: 'De um simulado para o outro.', itens: [
    { path: '/teacher/reports/descriptor-evolution', label: 'Evolução por Habilidade', frase: 'Como cada habilidade mudou de um simulado para o outro' },
    { path: '/teacher/reports/proficiency-evolution', label: 'Evolução do Desempenho', frase: 'A taxa de acerto de cada aluno ao longo dos simulados' },
  ] },
  { coluna: 2, titulo: 'Exportar', frase: 'Os dados, para a planilha.', itens: [
    { path: '/teacher/reports/item-responses', label: 'Exportar respostas (CSV)', frase: 'Uma linha por aluno e por questão' },
  ] },
];

/**
 * OS RELATÓRIOS DA GESTÃO — a mesma organização, com os endereços de `/gestao`; menos "Próximo Simulado" (planejamento de
 * quem leciona) e "Por Aluno" (a conversa do professor com o aluno dele), e por isso sem as perguntas "Quem precisa de
 * ajuda?" e "O que fazer agora?". Lida pela barra lateral da gestão e pela página de relatórios dela (`/gestao/relatorios`).
 */
export const GRUPOS_DE_RELATORIO_DA_GESTAO: GrupoDeRelatorio[] = [
  { coluna: 1, titulo: 'Como foi a prova?', frase: 'O resultado de uma aplicação, de cada turma e de cada aluno.', itens: [
    { path: '/gestao/relatorios/por-simulado', label: 'Por Simulado', frase: 'Participação, médias, questões e distratores de uma aplicação' },
    { path: '/gestao/relatorios/proficiencia', label: 'Níveis de Desempenho', frase: 'Quantos alunos em cada faixa de acerto' },
    { path: '/gestao/relatorios/notas', label: 'Notas por Turma', frase: 'As notas de cada aluno em cada simulado, com CSV' },
  ] },
  { coluna: 2, titulo: 'O que a turma não aprendeu?', frase: 'O acerto por habilidade — SAEB, BNCC, Enem ou conteúdo.', itens: [
    { path: '/gestao/relatorios/descritores', label: 'Por Habilidade', frase: 'O acerto por habilidade numa aplicação' },
    { path: '/gestao/relatorios/mapa', label: 'Mapa de Habilidades', frase: 'Habilidade × turma, em cores' },
    { path: '/gestao/relatorios/comparar-turmas', label: 'Comparar Turmas', frase: 'As turmas lado a lado no mesmo simulado' },
  ] },
  { coluna: 1, titulo: 'Está melhorando?', frase: 'De um simulado para o outro.', itens: [
    { path: '/gestao/relatorios/evolucao-descritor', label: 'Evolução por Habilidade', frase: 'Como cada habilidade mudou de um simulado para o outro' },
    { path: '/gestao/relatorios/evolucao-proficiencia', label: 'Evolução do Desempenho', frase: 'A taxa de acerto de cada aluno ao longo dos simulados' },
  ] },
  { coluna: 2, titulo: 'Exportar', frase: 'Os dados, para a planilha.', itens: [
    { path: '/gestao/relatorios/respostas', label: 'Exportar respostas (CSV)', frase: 'Uma linha por aluno e por questão' },
  ] },
];
